import { type PointerEvent, type RefObject, useRef, useState } from 'react';

type Pan = { pointerId: number; startX: number; startY: number; scrollLeft: number; scrollTop: number };

/**
 * Drag with the mouse to scroll `scrollRef`, for zoomed previews whose scrollbars are hidden.
 * `canStart` can turn a press down, e.g. one on text the reader may want to select.
 */
export function useDragToPan(
  scrollRef: RefObject<HTMLElement | null>,
  canStart: (target: EventTarget) => boolean = () => true,
) {
  const [isPanning, setIsPanning] = useState(false);
  const panRef = useRef<Pan | null>(null);

  const end = (e: PointerEvent<HTMLElement>) => {
    if (panRef.current?.pointerId !== e.pointerId) return;
    panRef.current = null;
    setIsPanning(false);
  };

  const panHandlers = {
    onPointerDown: (e: PointerEvent<HTMLElement>) => {
      const scroller = scrollRef.current;
      // Touch already scrolls natively.
      if (!scroller || e.pointerType === 'touch' || e.button !== 0 || !canStart(e.target)) return;
      if (scroller.scrollWidth <= scroller.clientWidth && scroller.scrollHeight <= scroller.clientHeight) return;
      panRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        scrollLeft: scroller.scrollLeft,
        scrollTop: scroller.scrollTop,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
      // Stops the drag from starting a text selection.
      e.preventDefault();
      setIsPanning(true);
    },
    onPointerMove: (e: PointerEvent<HTMLElement>) => {
      const pan = panRef.current;
      const scroller = scrollRef.current;
      if (!pan || pan.pointerId !== e.pointerId || !scroller) return;
      scroller.scrollLeft = pan.scrollLeft - (e.clientX - pan.startX);
      scroller.scrollTop = pan.scrollTop - (e.clientY - pan.startY);
    },
    onPointerUp: end,
    onPointerCancel: end,
  };

  return { isPanning, panHandlers };
}
